import { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlusCircle, Edit, Trash2, Search, Eye, Clock, Send, FileText } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Customer } from "@/types/models";
import { toast } from "sonner";
import CustomerDetailsModal from "@/components/customers/CustomerDetailsModal";
import RentalHistoryModal from "@/components/customers/RentalHistoryModal";
import AddCustomerModal from "@/components/rentals/AddCustomerModal";
import { deleteCustomer, sendWhatsappMessage, uploadContractPdf } from "@/integrations/supabase/services";
import { generateContractPdf } from "@/lib/contractGenerator";

const Customers = () => {
  const [search, setSearch] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isRentalHistoryModalOpen, setIsRentalHistoryModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [sendingContractId, setSendingContractId] = useState<string | null>(null);
  const [contractResendTarget, setContractResendTarget] = useState<Customer | null>(null);
  const limit = 20; // Cantidad de clientes por página

  const queryClient = useQueryClient();

  const handleSendContract = async (customer: Customer) => {
    setSendingContractId(customer.id);
    try {
      let contractUrl = customer.contract_url;

      if (!contractUrl) {
        if (!customer.id_photo_url) {
          toast.error("El cliente no tiene una foto de cédula registrada. Por favor, edítelo para agregarla antes de generar el contrato.");
          return;
        }

        toast.info("Generando contrato personalizado...");
        
        const blob = await generateContractPdf({
          name: customer.name,
          idCard: customer.id_card,
          phone: customer.phone,
          idPhotoUrl: customer.id_photo_url
        });

        const contractFileName = `contract-${customer.id_card}-${Date.now()}.pdf`;
        contractUrl = await uploadContractPdf(blob, contractFileName);

        // Actualizar cliente en base de datos
        await supabase
          .from('customers')
          .update({ contract_url: contractUrl, contract_sent: true })
          .eq('id', customer.id);
        
        queryClient.invalidateQueries({ queryKey: ['customers'] });
      }

      toast.info("Enviando contrato por WhatsApp...");
      await sendWhatsappMessage({
        cardID: customer.id_card,
        template: "welcome",
        variables: [customer.name],
        contract: true,
        contractUrl: contractUrl || undefined
      });

      toast.success("Contrato enviado exitosamente por WhatsApp");
    } catch (err: any) {
      console.error("Error al enviar el contrato:", err);
      toast.error("Error al enviar el contrato: " + err.message);
    } finally {
      setSendingContractId(null);
    }
  };

  const { data: customers = [], isLoading, isFetching } = useQuery({
    queryKey: ['customers', page, search],
    queryFn: async () => {
      let query = supabase
        .from('customers')
        .select('*')
        .order('name')
        .range((page - 1) * limit, page * limit - 1);

      if (search) {
        if (/^\d+$/.test(search)) {
          query = query.or(`id_card.eq.${search}`);
        } else {
          query = query.or(`name.ilike.%${search}%`);
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Customer[];
    },
    staleTime: 60000,
  });

  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      let query = supabase
        .from('customers')
        .select('*', { count: 'exact', head: true });

      if (search) {
        if (/^\d+$/.test(search)) {
          query = query.or(`id_card.eq.${search}`);
        } else {
          query = query.or(`name.ilike.%${search}%`);
        }
      }

      const { count, error } = await query;
      if (!error && typeof count === 'number') setTotalCount(count);
    };
    fetchCount();
  }, [search]);

  const totalPages = Math.ceil(totalCount / limit);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const deleteMutation = useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success("Cliente eliminado exitosamente");
    },
    onError: (error) => {
      toast.error("Error al eliminar cliente: " + error.message);
    },
  });

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm("¿Está seguro de que desea eliminar este cliente?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleViewDetails = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsDetailsModalOpen(true);
  };

  const handleViewRentalHistory = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsRentalHistoryModalOpen(true);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['customers'] });
    setIsCreateDialogOpen(false);
    setIsEditDialogOpen(false);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Clientes</h1>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Nuevo Cliente
          </Button>
        </div>

        <AddCustomerModal
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onSuccess={handleSuccess}
        />

        {editingCustomer && (
          <AddCustomerModal
            open={isEditDialogOpen}
            onOpenChange={setIsEditDialogOpen}
            onSuccess={handleSuccess}
            customerToEdit={editingCustomer}
          />
        )}

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Buscar clientes..."
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cédula</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Contrato</TableHead>
                <TableHead className="w-[200px] text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading || isFetching ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center h-24">
                    Cargando clientes...
                  </TableCell>
                </TableRow>
              ) : customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center h-24">
                    No se encontraron clientes.
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow key={customer.id} className="hover:bg-gray-100">
                    <TableCell className="py-2">{customer.id_card}</TableCell>
                    <TableCell className="py-2">{customer.name}</TableCell>
                    <TableCell className="py-2">{customer.phone}</TableCell>
                    <TableCell className="py-2">
                      {customer.contract_url ? (
                        <a
                          href={customer.contract_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-850 hover:bg-emerald-250 transition-colors"
                        >
                          <FileText size={12} className="text-emerald-700" />
                          Enviado
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-650">
                          No enviado
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleViewRentalHistory(customer)}
                          title="Ver historial de alquileres"
                        >
                          <Clock size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleViewDetails(customer)}
                          title="Ver detalles"
                        >
                          <Eye size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleEdit(customer)}
                          title="Editar"
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                          onClick={() => (customer.contract_url ? setContractResendTarget(customer) : handleSendContract(customer))}
                          disabled={sendingContractId === customer.id}
                          title={customer.contract_url ? "Reenviar contrato por WhatsApp" : "Enviar contrato por WhatsApp"}
                        >
                          {sendingContractId === customer.id ? (
                            <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Send size={16} />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(customer.id)}
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex justify-between items-center">
          <Button variant="outline" disabled={page === 1} onClick={handlePrevPage}>
            Anterior
          </Button>
          <span>Página {page} de {totalPages}</span>
          <Button variant="outline" disabled={page === totalPages || totalPages === 0} onClick={handleNextPage}>
            Siguiente
          </Button>
        </div>

        <CustomerDetailsModal
          customer={selectedCustomer}
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
        />

        <RentalHistoryModal
          customer={selectedCustomer}
          isOpen={isRentalHistoryModalOpen}
          onClose={() => setIsRentalHistoryModalOpen(false)}
        />

        <AlertDialog open={contractResendTarget !== null} onOpenChange={(open) => !open && setContractResendTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Reenviar contrato?</AlertDialogTitle>
              <AlertDialogDescription>
                {contractResendTarget?.name} ya tiene un contrato enviado. Se generará un nuevo mensaje de WhatsApp para volver a enviarlo.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  const customer = contractResendTarget;
                  setContractResendTarget(null);
                  if (customer) handleSendContract(customer);
                }}
              >
                Reenviar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
};

export default Customers;
